import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, Package, Download, ShoppingBag, Minus, Plus, Star } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, itDate, errMsg } from "@/lib/download";

export const Stars = ({ value, count, size = "w-3.5 h-3.5" }) => (
  <span className="inline-flex items-center gap-0.5" data-testid="stars">
    {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`${size} ${value >= n - 0.25 ? "fill-[#E2A453] text-[#E2A453]" : "text-slate-300"}`}/>)}
    {count !== undefined && <span className="text-xs text-slate-500 ml-1">{value ? `${value} (${count})` : "Nessuna recensione"}</span>}
  </span>
);

export const BuyBox = ({ p, onBought }) => {
  const { user } = useAuth();
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const out = p.kind === "physical" && p.stock !== null && p.stock <= 0;
  if (!user) { return <Link to="/login" data-testid={`catalog-login-${p.id}`} className="btn-pill-outline !py-2 text-xs justify-center w-full">Accedi per acquistare</Link>; }
  if (user.id === p.sellerUserId) { return <span className="text-xs text-slate-500">È un tuo prodotto</span>; }
  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/market/catalog/${p.id}/buy`, { qty });
      toast.success(`Acquisto simulato completato: ${p.title}${r.data.order.digitalInfo ? ` · ${r.data.order.digitalInfo}` : ""}`, { duration: 8000 });
      onBought();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      {p.kind === "physical" && <div className="flex items-center border border-slate-300 rounded-full">
        <button onClick={() => setQty(Math.max(1, qty - 1))} aria-label="Meno" className="p-1.5"><Minus className="w-3 h-3"/></button>
        <span className="text-sm w-6 text-center" data-testid={`catalog-qty-${p.id}`}>{qty}</span>
        <button onClick={() => setQty(Math.min(p.stock ?? 50, qty + 1))} aria-label="Più" className="p-1.5"><Plus className="w-3 h-3"/></button></div>}
      <button disabled={busy || out} onClick={buy} data-testid={`catalog-buy-${p.id}`} className="btn-pill-gold !py-2 text-xs flex-1 justify-center disabled:opacity-50"><ShoppingBag className="w-3.5 h-3.5"/>{out ? "Esaurito" : "Acquista"}</button>
    </div>
  );
};

export const ProductCard = ({ p, onBought }) => (
  <article className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:shadow-xl hover:-translate-y-1 transition-[transform,box-shadow] duration-300" data-testid={`catalog-item-${p.id}`}>
    <Link to={`/store/prodotto/${p.id}`} className="relative aspect-[4/3] bg-[#F5F7FB] block" data-testid={`catalog-link-${p.id}`}>
      {p.image ? <img src={mediaUrl(p.image)} alt={p.title} loading="lazy" className="w-full h-full object-cover"/> : <div className="w-full h-full flex items-center justify-center">{p.kind === "digital" ? <Download className="w-10 h-10 text-slate-300"/> : <Package className="w-10 h-10 text-slate-300"/>}</div>}
      <span className="absolute left-3 top-3 text-[10px] font-bold px-2.5 py-1 rounded-full bg-white/90 text-aic-navy">{p.kind === "digital" ? "Digitale" : "Fisico"}</span>
      {p.images?.length > 1 && <span className="absolute right-3 top-3 text-[10px] font-bold px-2 py-1 rounded-full bg-[#0D2B5A]/80 text-white">{p.images.length} foto</span>}
    </Link>
    <div className="p-4 flex-1 flex flex-col">
      <Link to={`/store/prodotto/${p.id}`} className="font-display font-bold text-aic-navy leading-snug hover:text-[#C98A3C]">{p.title}</Link>
      <div className="text-xs text-slate-500 mt-0.5">di {p.sellerName}</div>
      <div className="mt-1"><Stars value={p.rating} count={p.reviewCount}/></div>
      <p className="text-sm text-slate-600 mt-2 line-clamp-3 flex-1">{p.description}</p>
      <div className="flex items-end justify-between mt-3 mb-3"><b className="font-display text-xl text-aic-navy" data-testid={`catalog-price-${p.id}`}>{eur(p.price)}</b>
        {p.kind === "physical" && p.stock !== null && <span className="text-xs text-slate-500">{p.stock > 0 ? `${p.stock} disponibili` : "Esaurito"}</span>}</div>
      <BuyBox p={p} onBought={onBought}/>
    </div>
  </article>
);

export const MyOrders = ({ path, title, testId, seller }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(path).then((r) => setRows(r.data)).catch(() => {}); }, [path]);
  if (!rows.length) { return null; }
  return (
    <div className="card-premium p-6 mt-8" data-testid={testId}>
      <h2 className="font-display text-xl font-bold mb-3">{title}</h2>
      <div className="divide-y divide-slate-100 text-sm">{rows.map((o) => (
        <div key={o.id} className="py-2 flex flex-wrap justify-between gap-2"><span><b>{o.title}</b> x{o.qty || 1} · {itDate(o.createdAt)}{seller && o.buyerName ? ` · ${o.buyerName}` : ""}{!seller && o.digitalInfo ? ` · ${o.digitalInfo}` : ""}</span>
          <span>{eur(o.price)}{seller && <span className="text-xs text-slate-500"> · a te {eur(o.sellerNet)}</span>}</span></div>))}</div>
    </div>
  );
};

export const StoreCatalog = ({ onBought }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [sort, setSort] = useState("new");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => api.get("/market/catalog", { params: { q: q || undefined, kind: kind || undefined, sort } }).then((r) => setItems(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, kind, sort, tick]);
  const sel = "border border-slate-300 rounded-xl px-4 py-2.5 text-sm";
  return (
    <section data-testid="store-catalog">
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-[220px]"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca strumenti, attrezzature, corsi…" data-testid="catalog-search" className={`${sel} w-full pl-9 focus:outline-none focus:border-gold`}/></div>
        <select value={kind} onChange={(e) => setKind(e.target.value)} data-testid="catalog-kind" className={sel}><option value="">Tutti i prodotti</option><option value="physical">Prodotti fisici</option><option value="digital">Prodotti digitali</option></select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort" className={sel}><option value="new">Più recenti</option><option value="price_asc">Prezzo crescente</option><option value="price_desc">Prezzo decrescente</option></select>
      </div>
      {items === null ? <p className="text-slate-500">Caricamento…</p> : items.length === 0 ? <p className="text-slate-500" data-testid="catalog-empty">Nessun prodotto disponibile al momento.</p>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{items.map((p) => <ProductCard key={p.id} p={p} onBought={() => { setTick(tick + 1); onBought?.(); }}/>)}</div>}
    </section>
  );
};
